// Pure functions extracted from user-data.js for testability.
// No Firebase/Firestore/localStorage imports — just data transforms.

/**
 * Merge local and cloud recent binders lists.
 * Cloud entries take precedence unless a local entry has a newer lastAccessed timestamp.
 * Deduplicates by URL (case-insensitive), sorted by lastAccessed descending.
 * @param {Array<{ url: string, lastAccessed?: number }>} localBinders
 * @param {Array<{ url: string, lastAccessed?: number }>} cloudBinders
 * @param {number} maxBinders - Maximum number of binders to return
 * @returns {Array} Merged, sorted, capped list
 */
export function mergeBinders(localBinders, cloudBinders, maxBinders) {
  const binderMap = new Map();
  for (const binder of cloudBinders) {
    binderMap.set(binder.url.toLowerCase(), binder);
  }
  for (const binder of localBinders) {
    const key = binder.url.toLowerCase();
    const existing = binderMap.get(key);
    if (
      !existing ||
      (binder.lastAccessed &&
        (!existing.lastAccessed || binder.lastAccessed > existing.lastAccessed))
    ) {
      binderMap.set(key, binder);
    }
  }
  const merged = Array.from(binderMap.values());
  merged.sort((a, b) => (b.lastAccessed || 0) - (a.lastAccessed || 0));
  return merged.slice(0, maxBinders);
}

/**
 * Calculate exchange progress for a set of trade cards.
 * Only considers cards with status 'trade' (or no status, which defaults to 'trade').
 * @param {Array<{ status?: string, quantity?: number, exchangedQuantity?: number }>} cards
 * @returns {{ exchanged: number, total: number, complete: boolean }}
 */
export function getExchangeProgress(cards) {
  if (!cards || cards.length === 0) return { exchanged: 0, total: 0, complete: true };
  const tradeCards = cards.filter((c) => (c.status || 'trade') === 'trade');
  let totalQuantity = 0;
  let exchangedQuantity = 0;
  for (const card of tradeCards) {
    const qty = card.quantity || 1;
    totalQuantity += qty;
    exchangedQuantity += Math.min(card.exchangedQuantity || 0, qty);
  }
  return {
    exchanged: exchangedQuantity,
    total: totalQuantity,
    complete: exchangedQuantity >= totalQuantity,
  };
}

/**
 * Merge local and cloud claimed binders (myBinders).
 * Cloud URLs take precedence. Unique local entries are appended up to max.
 * @param {Array<{ url: string }>} localMyBinders
 * @param {Array<{ url: string }>} cloudMyBinders
 * @param {number} maxMyBinders
 * @returns {Array} Merged list, capped at maxMyBinders
 */
export function mergeMyBinders(localMyBinders, cloudMyBinders, maxMyBinders) {
  const cloudUrls = new Set(cloudMyBinders.map((b) => (b.url || '').toLowerCase()));
  const toAdd = localMyBinders.filter((b) => !cloudUrls.has((b.url || '').toLowerCase()));
  return [...cloudMyBinders, ...toAdd].slice(0, maxMyBinders);
}
